import { ScrollReveal } from '@/components/effects/ScrollReveal';
import { ShieldCheck, Clock, Award, Users } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const pillars = [
  { icon: ShieldCheck, title: 'Security-First', desc: 'Every service built on security fundamentals. We protect what we build.', stat: '99.97% uptime' },
  { icon: Clock, title: 'Rapid Delivery', desc: 'Expert execution without enterprise timelines. Results in weeks, not quarters.', stat: '<2min response' },
  { icon: Award, title: 'Proven Track', desc: 'Established London practice with demonstrated outcomes across all four areas of expertise.', stat: '480hrs curriculum' },
  { icon: Users, title: 'Client-First', desc: 'Dedicated support team. You get a partner, not a ticket number.', stat: '4.9/5 rating' },
];

const chartData = [
  { year: '2019', value: 5.3 },
  { year: '2020', value: 7.1 },
  { year: '2021', value: 9.4 },
  { year: '2022', value: 11.2 },
  { year: '2023', value: 13.0 },
  { year: '2024', value: 15.8 },
  { year: '2025', value: 18.2 },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="px-3 py-2 rounded-lg font-mono text-xs"
        style={{
          background: 'rgba(10,10,18,0.95)',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--b2)',
        }}
      >
        <p style={{ color: 'var(--ts)' }}>{label}</p>
        <p style={{ color: 'var(--a)' }}>£{payload[0].value}bn</p>
      </div>
    );
  }
  return null;
};

export const WhySection = () => {
  return (
    <section
      id="why"
      data-testid="why-acodia-section"
      className="py-14 sm:py-20"
      style={{ background: 'var(--l1)' }}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="font-body text-xs uppercase tracking-[0.22em] mb-3" style={{ color: 'var(--a)' }}>
              Why Acodia
            </p>
            <h2
              className="font-display font-extrabold tracking-[-0.03em] mb-4"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.8rem)', color: 'var(--white)' }}
            >
              Built different.
            </h2>
            <p className="font-body font-light text-base" style={{ color: 'var(--tm)', lineHeight: '1.72' }}>
              A specialist practice, not an agency. Expert-level services at a fraction of enterprise cost.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-16">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <ScrollReveal key={p.title} delay={i * 0.065}>
                <div
                  data-testid={`why-pillar-${i}`}
                  className="p-6 rounded-[10px] transition-all duration-300 group text-center"
                  style={{
                    background: 'rgba(10,10,18,0.88)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid var(--b1)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--b3)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--b1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Icon size={24} style={{ color: 'var(--a)' }} className="mb-4 mx-auto block" />
                  <h3 className="font-display text-base font-bold mb-2" style={{ color: 'var(--white)' }}>{p.title}</h3>
                  <p className="font-body text-sm font-light mb-4" style={{ color: 'var(--tm)', lineHeight: '1.65' }}>{p.desc}</p>
                  <p className="font-mono text-xs" style={{ color: 'var(--a)' }}>{p.stat}</p>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        {/* UK Cyber Market Chart */}
        <ScrollReveal delay={0.2}>
          <div
            data-testid="uk-market-chart"
            className="p-6 rounded-lg"
            style={{ background: 'rgba(10,10,18,0.88)', border: '1px solid var(--b1)' }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="font-body text-xs uppercase tracking-[0.22em]" style={{ color: 'var(--ts)' }}>UK Cyber Security Market</p>
                <p className="font-display text-lg font-bold mt-1" style={{ color: 'var(--white)' }}>£18.2bn by 2025</p>
              </div>
              <div className="font-mono text-xs px-3 py-1 rounded-full" style={{ background: 'rgba(74,222,128,0.10)', color: 'var(--ok)' }}>+14% YoY</div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="purpleGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="year" tick={{ fill: 'rgba(240,237,248,0.38)', fontSize: 11, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'rgba(240,237,248,0.15)', fontSize: 11, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="value" stroke="#8B5CF6" strokeWidth={2} fill="url(#purpleGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};
